import {
  Box,
  Stack,
  Typography,
  Tabs,
  Tab,
  useTheme,
  Paper,
  useMediaQuery,
} from '@mui/material';
import { ArticlesSection } from '../components/Article/ArticlesSection';
import useDocumentTitle from '../hooks/useDocumentTitle';
import ErrorView from '../components/ErrorViews/ErrorView';
import StarIcon from '@mui/icons-material/Star';
import ArticlesSectionSkeleton from '../components/Article/ArticlesSectionSkeleton';
import { useTranslation } from 'react-i18next';
import CategoryGrid from '../components/Category/CategoryGrid';
import { CONTENT_TYPE, Category, apiStates } from '../types/types';
import { JaaRoute, getRouterPathToContentIndex } from '../utilities/Links';
import { getPrimaryCategories } from '../utilities/Categories';
import { useMemo, useState, useContext } from 'react';
import AboutJaaSection from '../components/Promotional/AboutJaaSection';
import OpenGraphGeneral from '../components/MetaData/OpenGraphGeneral';
import { articleCardLocation } from '../utilities/gtm';
import BrandHeading from '../components/Page/BrandHeading';
import TabPanelContentArea from '../components/FrontPage/TabPanelContentArea';
import TabPanel from '../components/Tabs/TabPanel';
import { UseArticlesResult, useArticles } from '../hooks/useArticles';
import NewsCarousel from '../components/News/Carousel/NewsCarousel';
import PageHeader from '../components/Page/PageHeader';
import { AuthContext } from '../components/Auth/AuthProvider';

function a11yProps(index: number) {
  return {
    id: `tab-${index}`,
    'aria-controls': `tabpanel-${index}`,
  };
}

export default function FrontPage() {
  const { t } = useTranslation();
  useDocumentTitle('Etusivu');

  const theme = useTheme();
  const showCarouselVariant = useMediaQuery(theme.breakpoints.down('lg'));

  const articleLimit = 9;

  const [tabValue, setTabValue] = useState<number>(0);

  const { authInfo } = useContext(AuthContext);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const {
    articles: newArticles,
    apiError: newArticlesApiError,
    state: newArticlesState,
  }: UseArticlesResult = useArticles({
    limit: articleLimit,
    contentType: [
      CONTENT_TYPE.Article,
      CONTENT_TYPE.Overview,
      CONTENT_TYPE.ShortLegalNews,
      CONTENT_TYPE.CaseCommentary,
    ],
  });

  const { articles: upcomingArticles, state: upcomingArticlesState } =
    useArticles({ upcoming: true, limit: 3 });
  const orderedUpcomingArticles = useMemo(() => {
    return Array.from(upcomingArticles);
  }, [upcomingArticles]);

  const {
    articles: recommendedArticles,
    apiError: recommendedArticlesApiError,
    state: recommendedArticlesState,
  } = useArticles({ limit: 6, orderBy: 'featureDocument' });

  // If category is not matching any known categories, show "Content not found" view
  if (newArticlesApiError || recommendedArticlesApiError) {
    return (
      <Box
        sx={{
          paddingX: { xs: 2, sm: 0 },
        }}
      >
        <ErrorView
          apiError={newArticlesApiError ?? recommendedArticlesApiError}
        />
      </Box>
    );
  }

  const primaryCategoriesString = getPrimaryCategories();

  // Format primary categories for the CategoryGrid component
  const primaryCategoriesFormatted: Category[] = primaryCategoriesString.map(
    (category) => {
      return {
        name: category,
        path: getRouterPathToContentIndex(category),
      };
    },
  );

  primaryCategoriesFormatted.push({
    name: t('categoryLinks.seeAll'),
    path: JaaRoute.contentIndex,
  });

  return (
    <Box>
      <PageHeader title={t('service.title')} screenreaderOnly />
      <Box
        sx={{
          marginBottom: 6,
        }}
      >
        <NewsCarousel />
      </Box>
      <Stack direction='column' spacing={{ sm: 2 }} component='section'>
        <Box sx={{ flexGrow: 1 }}>
          <Stack
            direction='row'
            sx={{
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: 2,
              borderBottom: 1,
              borderColor: 'divider',
            }}
          >
            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              aria-label={t('frontPage.tabs.label')}
              textColor='inherit'
            >
              <Tab
                label={t('frontPage.newContent')}
                component='div'
                sx={{
                  textTransform: 'none',
                  fontSize: theme.typography.h2.fontSize,
                  fontWeight: theme.typography.h2.fontWeight,
                }}
                {...a11yProps(0)}
              />
              <Tab
                label={t('frontPage.upcomingContent')}
                component='div'
                sx={{
                  textTransform: 'none',
                  fontSize: theme.typography.h2.fontSize,
                  fontWeight: theme.typography.h2.fontWeight,
                }}
                {...a11yProps(1)}
              />
            </Tabs>
          </Stack>

          <TabPanel index={0} value={tabValue}>
            <TabPanelContentArea
              articles={newArticles}
              location={articleCardLocation.LATEST}
              variant={showCarouselVariant ? 'carousel' : 'grid'}
              state={newArticlesState}
              articleLimit={articleLimit}
              route={JaaRoute.contentIndex}
            />
          </TabPanel>
          <TabPanel index={1} value={tabValue}>
            <TabPanelContentArea
              articles={orderedUpcomingArticles}
              location={articleCardLocation.INCOMING}
              variant={showCarouselVariant ? 'carousel' : 'grid'}
              state={upcomingArticlesState}
              articleLimit={articleLimit}
              emptyView={
                <Paper
                  variant='outlined'
                  sx={{
                    paddingTop: { xs: 4, md: 6 },
                    paddingX: 2,
                    paddingBottom: { xs: 4, sm: 6, md: 10 },
                  }}
                >
                  <Typography variant='h3'>
                    {t('frontPage.tabs.upcoming.empty.title')}
                  </Typography>
                  <Typography variant='body1'>
                    {t('frontPage.tabs.upcoming.empty.description')}
                  </Typography>
                </Paper>
              }
            />
          </TabPanel>

          <Stack
            direction='row'
            sx={{
              justifyContent: 'flex-start',
              alignItems: 'center',
              marginTop: 6,
              marginBottom: 2,
            }}
          >
            <BrandHeading variant='h2'>
              {t('frontPage.staffPicks')}
            </BrandHeading>
            <StarIcon
              fontSize='small'
              sx={{ color: '#ffb400', marginLeft: 1 }}
            />
          </Stack>
          {recommendedArticlesState === apiStates.READY && (
            <ArticlesSection
              articles={recommendedArticles}
              location={articleCardLocation.STAFF_PICKS}
              headingLevel='h3'
              showGrid
            />
          )}
          {recommendedArticlesState === apiStates.FETCHING && (
            <ArticlesSectionSkeleton showGrid />
          )}
          <BrandHeading variant='h2' marginTop={6} marginBottom={2}>
            {t('categoryLinks.title')}
          </BrandHeading>
          <Box
            sx={{
              marginBottom: 6,
            }}
          >
            <CategoryGrid categories={primaryCategoriesFormatted} />
          </Box>
        </Box>
      </Stack>

      {!authInfo?.almaIdRights && !authInfo?.ipRights && <AboutJaaSection />}
      <OpenGraphGeneral />
    </Box>
  );
}
